// A list of publications. Shared by the job screen's panel (coverage produced by
// this job) and the client card (everything a client received, across all jobs).
//
// `showJob` adds the job column: on the client card the job is context worth seeing
// per row, while on the job screen it would repeat the page title.
import he from '../i18n/he';
import DataTable from './DataTable';
import DateText from './DateText';
import FileList from './FileList';
import IconButton from './IconButton';
import { EditIcon, TrashIcon } from './icons';

// Where the piece ran. Dates and links live here; the screenshots are the files
// column, which names its own outlet — so an outlet still missing its screenshot is
// visible as one listed here that has no row over there.
export const OutletChips = ({ outlets }) => {
  if (!outlets || outlets.length === 0) return <span className="muted">{he.publications.noOutlets}</span>;
  return (
    <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
      {outlets.map((o) => (
        <span key={o.outlet_code} className="meta-chip">
          {o.url
            ? <a href={o.url} target="_blank" rel="noopener noreferrer">{o.outlet_label}</a>
            : o.outlet_label}
          {o.ran_on && <> · <DateText value={o.ran_on} /></>}
        </span>
      ))}
    </div>
  );
};

export default function PublicationList({ publications, showJob, onEdit, onDelete, empty }) {
  const columns = [
    { key: 'title', label: he.publications.name, primary: true },
    { key: 'outlets', label: he.publications.outlets, render: (r) => <OutletChips outlets={r.outlets} /> },
    ...(showJob ? [{ key: 'job_title', label: he.publications.job, render: (r) => r.job_title || '—' }] : []),
    {
      key: 'published_date',
      label: he.publications.date,
      render: (r) => <DateText value={r.published_date} />,
    },
    {
      // One flat row per file, each naming its outlet and carrying view + download,
      // then the publication's own edit / delete as one trailing group. All the row's
      // actions live in this single cell so they read as one toolbar. Delete removes
      // the WHOLE publication — it is the trash icon, styled red (danger), set off
      // below the files so it never looks like it removes just one screenshot.
      // Identical on the job screen and the client card — a file is usable wherever
      // it is listed, never only from the form that uploaded it.
      key: 'files',
      label: he.publications.files,
      render: (r) => (
        <FileList
          files={r.files}
          trailing={(onEdit || onDelete) && (
            <>
              {onEdit && (
                <IconButton icon={<EditIcon />} label={he.common.edit} onClick={() => onEdit(r)} />
              )}
              {onDelete && (
                <IconButton
                  icon={<TrashIcon />}
                  label={he.publications.deletePublication}
                  danger
                  onClick={() => onDelete(r)}
                />
              )}
            </>
          )}
        />
      ),
    },
  ];

  return <DataTable columns={columns} rows={publications} empty={empty} />;
}
